/* Device fingerprint capture (tool 'wedge_device_fp' in AIUsageLog).
   Browser-sourced timezone + viewport + DPR + language, sent ONCE per
   session for a signed-in rep so activation can be segmented by where the
   rep is and what they're holding.

   2026-09-29 · moved out of main.tsx. The old boot-time sendBeacon fired
   1.5s after every page load whether or not anyone was signed in, and a
   beacon cannot carry the wedge Bearer token, so only a still-valid
   access_token cookie could authenticate it (absent on iOS standalone PWAs,
   expired for any rep back after the access TTL). Prod nginx over two days:
   185 x 401 against 13 x 204. The sessionStorage gate was also set whether
   or not the send worked, so a failed send never retried in that session,
   and a signed-out headless harness (fresh context per load, so an empty
   gate every time) hit the 6/min throttle with 12 x 429.

   Now: mounted inside WedgeAuthGate (signed-in only), sent through
   fetchWithAuth (Bearer + one single-flight refresh on 401), one attempt per
   page load, and the session gate is set only on a 2xx. */
import { useEffect } from 'react';
import { fetchWithAuth } from '@territory/client/api';

export const DEVICE_FP_SENT_KEY = 'rv_wedge_device_fp_sent';
export const DEVICE_FP_URL = '/api/wedge/device-fingerprint';
const SEND_DELAY_MS = 1500;

export interface DeviceFingerprintEnv {
  timeZone?: string;
  screenWidth?: number;
  screenHeight?: number;
  innerWidth?: number;
  innerHeight?: number;
  devicePixelRatio?: number;
  language?: string;
}

/* Same validation the server applies in logDeviceFingerprint, so a payload
   the server would silently drop is never sent at all. */
export function buildDeviceFingerprintBody(env: DeviceFingerprintEnv): string | null {
  const tz = env.timeZone || '';
  if (!tz || !/^[A-Za-z_]+\/[A-Za-z_]+$/.test(tz)) return null;
  const w = Math.round(env.screenWidth || env.innerWidth || 0);
  const h = Math.round(env.screenHeight || env.innerHeight || 0);
  if (!w || !h) return null;
  return JSON.stringify({
    timezone: tz,
    viewport: `${w}x${h}`,
    dpr: Math.round((env.devicePixelRatio || 1) * 10) / 10,
    lang: (env.language || '').slice(0, 10),
  });
}

function readBrowserEnv(): DeviceFingerprintEnv {
  let timeZone = '';
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* no Intl */ }
  return {
    timeZone,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    language: typeof navigator !== 'undefined' ? navigator.language : '',
  };
}

function browserSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try { return window.sessionStorage; } catch { return null; }
}

export interface SendDeviceFingerprintDeps {
  fetchImpl?: (url: string, init?: RequestInit) => Promise<Response>;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  env?: DeviceFingerprintEnv;
}

export type DeviceFingerprintOutcome = 'sent' | 'already-sent' | 'attempted' | 'invalid' | 'failed';

/* One attempt per page load, even under StrictMode double effects or a
   remount of the auth gate. A failed attempt leaves the session gate unset,
   so the next page load tries again. */
let attemptedThisLoad = false;

export function resetDeviceFingerprintForTests(): void {
  attemptedThisLoad = false;
}

export async function sendDeviceFingerprintOnce(deps: SendDeviceFingerprintDeps = {}): Promise<DeviceFingerprintOutcome> {
  const storage = deps.storage !== undefined ? deps.storage : browserSessionStorage();
  try {
    if (storage?.getItem(DEVICE_FP_SENT_KEY) === '1') return 'already-sent';
  } catch { /* storage blocked: fall through, the per-load guard still caps it */ }
  if (attemptedThisLoad) return 'attempted';
  attemptedThisLoad = true;

  const body = buildDeviceFingerprintBody(deps.env ?? readBrowserEnv());
  if (!body) return 'invalid';

  try {
    const res = await (deps.fetchImpl ?? fetchWithAuth)(DEVICE_FP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    if (!res.ok) return 'failed';
  } catch {
    return 'failed';
  }
  try { storage?.setItem(DEVICE_FP_SENT_KEY, '1'); } catch { /* storage blocked */ }
  return 'sent';
}

/* Render inside WedgeAuthGate so it only mounts for a signed-in rep. Post-
   paint delay keeps it off the boot path. */
export function DeviceFingerprintBeacon(): null {
  useEffect(() => {
    const t = setTimeout(() => { void sendDeviceFingerprintOnce(); }, SEND_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  return null;
}
